import axios from "axios";
import { storeToRefs } from "pinia";
import { refreshToken } from "./auth";

// 创建axios实例
const instance = axios.create({
  baseURL: "/api",
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// 是否正在刷新token的标记
let isRefreshing = false;
// 重试队列，每一项将是一个待执行的函数形式
let requests = [];

// 请求拦截器
instance.interceptors.request.use(
  (config) => {
    // 从localStorage获取token
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);

// 响应拦截器
instance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response) {
      const { status, config: originalRequest } = error.response;

      // 如果是401未授权，尝试刷新token
      if (status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          // 如果已经在刷新中，将请求添加到队列
          return new Promise((resolve) => {
            requests.push((token) => {
              originalRequest.headers["Authorization"] = "Bearer " + token;
              resolve(instance(originalRequest));
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // 获取存储的令牌信息
          const token = localStorage.getItem("token");
          const refreshTokenValue = localStorage.getItem("refreshToken");

          if (token && refreshTokenValue) {
            // 尝试刷新token
            const response = await refreshToken(token, refreshTokenValue);

            if (response.success && response.token) {
              // 更新原始请求的授权头
              originalRequest.headers["Authorization"] =
                "Bearer " + response.token;

              // 执行队列中的所有请求
              requests.forEach((cb) => cb(response.token));
              requests = [];

              // 重试原始请求
              return instance(originalRequest);
            }
          }

          // 如果无法刷新token，则清除tokens并跳转到登录页
          console.warn("Failed to refresh token, redirecting to login page");
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("tokenExpiration");
          localStorage.removeItem("user");

          // 触发自定义事件，通知应用重定向到登录页
          window.dispatchEvent(new CustomEvent("auth:unauthorized"));
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);

          // 触发自定义事件，通知应用重定向到登录页
          window.dispatchEvent(new CustomEvent("auth:unauthorized"));
        } finally {
          isRefreshing = false;
        }
      }

      // 处理其他错误
      if (status === 400) {
        console.warn("Bad request:", error.response.data);
      } else if (status === 500) {
        console.error("Server error:", error.response.data);
      }
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Request setup error:", error.message);
    }

    return Promise.reject(error);
  }
);

// 监听自定义事件，处理未授权状态
window.addEventListener("auth:unauthorized", () => {
  // 在这里可以添加额外的逻辑，比如显示通知消息
  console.log("User session expired. Redirecting to login...");

  // 如果使用了vue-router，可以手动导航到登录页
  setTimeout(() => {
    window.location.href = "/login";
  }, 100);
});

// 封装常用HTTP方法
const http = {
  get(url, params = {}, config = {}) {
    return instance.get(url, { ...config, params });
  },
  
  post(url, data = {}, config = {}) {
    return instance.post(url, data, config);
  },
  
  put(url, data = {}, config = {}) {
    return instance.put(url, data, config);
  },
  
  delete(url, params = {}, config = {}) {
    return instance.delete(url, { ...config, params });
  },
  
  // 文件上传
  upload(url, file, data = {}, config = {}) {
    const formData = new FormData();
    formData.append('file', file);
    
    // 添加其他表单数据
    Object.keys(data).forEach(key => {
      formData.append(key, data[key]);
    });
    
    return instance.post(url, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(config.headers || {})
      }
    });
  },
  
  // 文件下载
  download(url, params = {}, filename = '') {
    return instance.get(url, {
      params,
      responseType: 'blob'
    }).then(response => {
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || `download-${Date.now()}`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(link);
    });
  }
};

export default http;
